import { computed, effect, inject, Injectable, untracked } from '@angular/core';

import { AppLocalStorageService } from 'src/app/features/shared/storage/services/local-storage-services/app-local-storage.service';
import { AuthService } from 'src/app/auth/services/auth.service';
import { ApplicationUserService } from 'src/app/infrastructure/services/application-user.services/application-user.service';
import { NavigationStackService } from 'src/app/features/shared/navigation-stack/services/navigation-stack.service';
import { DomainProfileService } from 'src/app/infrastructure/services/domain-profile-services/domain-profile.service';
import { parseJwt } from 'src/app/features/shared/storage/functions/parse-jwt';
import { pageShortLabel } from 'src/app/infrastructure/functions/page-label.function';
import { routeName } from 'src/app/infrastructure/services/route-service/models/route-name';
import { routeKey } from 'src/app/infrastructure/services/route-service/models/route-key';
import { routePage, TRoutePage } from 'src/app/infrastructure/services/route-service/models/route-page';
import { tabName, TabName } from 'src/app/infrastructure/services/page-services/models/tab-name';
import { pageName } from 'src/assets/common/enums/page-name.g';
import {
    ADMIN_CATEGORIES, DOMAIN_TAB_CATEGORY_OVERRIDES, IContainerTabSnapshot, INavHistoryEntry,
    isNavCategory, ITEM_TAB_CATEGORY_OVERRIDES, NAV_CATEGORY_LABEL, NAV_HISTORY_MAX_PER_GROUP, PROJECT_TAB_CATEGORY_OVERRIDES, TNavCategory,
    USER_L2_CATEGORIES, USER_L3_CATEGORIES, navCategory,
} from '../models/nav-history-entry';
import { INavTreeNode } from '../models/nav-tree-node';
import { navCategoryRoutePage } from '../models/nav-history-descriptors';

@Injectable({ providedIn: 'root' })
export class NavigationHistoryService {
    private readonly __store = inject(AppLocalStorageService);
    private readonly __authService = inject(AuthService);
    private readonly __navigationStackService = inject(NavigationStackService);
    private readonly __domainProfileService = inject(DomainProfileService);
    private readonly __applicationUserService = inject(ApplicationUserService);

    constructor() {
        // Account-wissel moet niet met andermans navigatiehistorie verderlopen. history/containerTabs
        // leven in dezelfde localStorage-boom als theme/taal (die WEL over een account-wissel heen
        // moeten blijven staan, zie AppLocalStorageService), en zijn origin- i.p.v. account-gescoped —
        // zonder deze wissel-detectie blijft op een gedeeld toestel de geschiedenis (incl. eventuele
        // admin-entries) van de vorige gebruiker gewoon zichtbaar voor de volgende.
        //
        // Vergelijkt bewust tegen een GEPERSISTEERD veld (appData$$.historyOwnerGuid), niet tegen een
        // in-memory instance-field: dat laatste (eerdere versie) miste de wissel zodra de vergelijking
        // voor het eerst in een HELEMAAL NIEUW tabblad gebeurde (bv. een password-reset-link die in
        // een verse tab opent) — zo'n tabblad heeft geen eigen herinnering aan de vorige gebruiker en
        // zag zijn eigen eerste login dus altijd als "gewone hydratie", ook als de localStorage-data
        // (gedeeld over ALLE tabbladen/sessies) feitelijk van een andere gebruiker was. Met het
        // gepersisteerde veld ziet ook een verse tab bij de eerste check al wiens data er nu ligt.
        // `undefined` (veld nog nooit geschreven, écht verse storage) is de enige uitzondering die
        // niet als wissel telt — dan is er niets om te wissen.
        //
        // Alleen bij een echte (niet-null) guid vergelijken: zonder deze guard telde ook de
        // logout-transitie zelf (__guid → null) al als "wissel" tegen de nog-ingevulde
        // historyOwnerGuid van de zojuist uitgelogde user, en wiste clear() de historie/
        // containerTabs dus al bij het uitloggen — vóórdat bekend is of dezelfde of een andere
        // user zich daarna opnieuw aanmeldt. Gevolg: ook een gewone re-login van dezelfde user
        // verloor zijn drawer-geschiedenis (o.a. de AdminPanel-containerTabs-snapshot, zie
        // adminTree$$), zichtbaar als "AdminPanel niet meer in de drawer na inloggen".
        effect(() => {
            const __token = this.__authService.token$$();
            const __guid = __token ? parseJwt(__token)?.nameid ?? null : null;
            if (!__guid) return;

            untracked(() => {
                const __storedOwner = this.__store.appData$$.historyOwnerGuid() as string | null | undefined;
                if (__storedOwner === __guid) return;

                const __isSwitch = __storedOwner !== undefined;
                this.__store.appData$$.historyOwnerGuid.set(__guid);

                if (__isSwitch) this.clear();
            });
        });
    }

    // Platte, al-gecapte lijst (SSOT = de user-boom, tak history). Gesynct naar backend.
    // Gededupliceerd op category+guid (nieuwste visitedAt wint): __navHistory_record bepaalt
    // dGuid/oGuid uit ApplicationUserService-state die bij een race nog niet is bijgetrokken
    // (zie feedback_pagestart_hydration_race_fix), waardoor hetzelfde record soms met een
    // afwijkende scope wordt geschreven. record()'s upsert matcht ook op dGuid/oGuid en mist
    // die tweede rij dan, wat een dubbele child-node (en NG0955 in de @for-tracking) oplevert.
    // __repairAdminScope herstelt daarna nog een tweede, inmiddels bij de bron gefixte
    // scope-fout (FunctionPageService navigeerde naar de domein- i.p.v. de admin-route, zie
    // feedback_function_page_service_route): admin-categorieën (Functions/Roles/...) horen
    // altijd dGuid=null te hebben, en oude entries met een dGuid worden hier alsnog gecorrigeerd
    // — inclusief het opruimen van een domein-wortel die daardoor als enige reden van bestaan had.
    readonly entries$$ = computed<INavHistoryEntry[]>(() =>
        this.__repairAdminScope(this.__repairSystemDomain(this.__dedupe(this.__store.appData$$.history() ?? []))));

    // Snapshot van geautoriseerde tabs per bezochte Domain/Project/AdminPanel (zie
    // _TabPageRouteComponent.__navHistory_recordTabs), keyed op 'admin' / `domain:{guid}` /
    // `project:{guid}`. Puur lokaal (niet gesynct), self-healing bij eerstvolgend bezoek.
    readonly containerTabs$$ = computed<Record<string, IContainerTabSnapshot[]>>(() =>
        this.__store.appData$$.containerTabs() ?? {}
    );

    // Admin-boom: één wortel (admin-domein, logo) → categorie-groepen → items. Categorie-koppen
    // komen bij voorkeur uit de live tab-snapshot (alle geautoriseerde tabs, ongeacht historie);
    // zonder snapshot (admin-panel zelf nog nooit bezocht) valt terug op de oude, historie-only lijst.
    readonly adminTree$$ = computed<INavTreeNode[]>(() => {
        const __adminLabel = pageShortLabel(pageName.adminPanelDetails);
        // Synthetische entry — alleen `url` telt voor deze wortel (navigatie + active-match); de
        // overige velden zijn niet van toepassing op dit niet-gehistoriseerde doel.
        const __entry: INavHistoryEntry = {
            category: navCategory.pages,
            guid: '',
            label: __adminLabel,
            photoGuid: null,
            logoFolderShard: null,
            logoVersion: null,
            dGuid: null,
            oGuid: null,
            // Route is `path: routeName.details` genest onder `/admin-panel` (adminRoutes redirect
            // '' → 'details'); router.url ná die redirect is dus `/admin-panel/details`, niet
            // `/admin-panel` — de url moet dat exact matchen (zie __pathToActive) om als actief
            // (en dus uitgeklapt) herkend te worden.
            url: `/${routeName.adminPanel}/${routeName.details}`,
            visitedAt: 0,
        };
        const __pickAdmin = (category: TNavCategory) => this.__pick(category, null, null);
        const __snapshot = this.containerTabs$$()['admin'];

        const __children = __snapshot?.length
            ? __snapshot.map(tab => this.__tabCategoryNode(tab, {}, __pickAdmin, 1, __entry, 'admin', routePage.adminPanel))
            : this.__fallbackCategoryNodes(ADMIN_CATEGORIES, __pickAdmin, 1, 'admin', { entry: __entry, routePage: routePage.adminPanel });

        // Wortel altijd teruggeven, ook zonder kinderen: zichtbaarheid is uitsluitend aan
        // isAdminPanel$$ (zie NavigationHistoryComponent._showAdmin$$). De 'admin'-snapshot is puur
        // lokaal — niet gesynct, gewist bij een account-wissel (clear()), afwezig op een nieuw
        // device/incognito — dus zonder (al bezochte) admin-historie verdween AdminPanel hier
        // anders volledig uit de drawer, tot de gebruiker 'm via Home opnieuw opende. Een kale
        // wortel is gewoon klikbaar (navigeert naar AdminPanel, dat de snapshot daarna weer vult).
        return this.__linkSiblings([{
            id: 'admin',
            label: __adminLabel,
            level: 0,
            logo: true,
            logoUrl: this.__domainProfileService.logoAppThumb_url$$(),
            entry: __entry,
            children: __children,
        }]);
    });

    // User-boom: domeinen (wortel) → tabs (categorie-koppen, incl. de "Projects"-tab) → items
    // (projects krijgen hun eigen level3-kinderen, zie __projectNode). Zelfde snapshot-eerst,
    // historie-only-fallback-opzet als de admin-boom hierboven.
    readonly userTree$$ = computed<INavTreeNode[]>(() => {
        const __entries = this.entries$$();
        const __snapshotMap = this.containerTabs$$();
        const __domains = this.__pick(navCategory.domains, null, null, __entries);

        const __roots = __domains.map(domain => {
            const __pickDomain = (category: TNavCategory) => this.__pick(category, domain.guid, null, __entries);
            const __snapshot = __snapshotMap[`domain:${domain.guid}`];

            const __children = __snapshot?.length
                ? __snapshot.map(tab => this.__tabCategoryNode(
                    tab, DOMAIN_TAB_CATEGORY_OVERRIDES, __pickDomain, 1, domain, `domain:${domain.guid}`, routePage.domain,
                    projects => projects.map(project => this.__projectNode(project, __entries)),
                ))
                : this.__domainFallbackChildren(domain, __pickDomain, __entries);

            const __logoUrl = this.__domainProfileService.domainLogoThumb_url_live(domain.guid, domain.logoFolderShard, domain.logoVersion);
            return {
                id: `domain:${domain.guid}`,
                label: domain.label,
                level: 0,
                entry: domain,
                logo: !!__logoUrl,
                logoUrl: __logoUrl,
                image: !__logoUrl,
                photoGuid: domain.photoGuid,
                children: __children,
            };
        });

        return this.__linkSiblings(__roots);
    });

    // Eén project-knoop onder de "Projects"-categorie: eigen entry/foto + eigen level3-kinderen
    // (projectleden/meldingen), zelfde snapshot-eerst-opzet als de containers hierboven.
    private __projectNode(project: INavHistoryEntry, entries: INavHistoryEntry[]): INavTreeNode {
        const __pickProject = (category: TNavCategory) => this.__pickByProject(category, project.guid, entries);
        const __snapshot = this.containerTabs$$()[`project:${project.guid}`];

        const __children = __snapshot?.length
            ? __snapshot.map(tab => this.__tabCategoryNode(tab, PROJECT_TAB_CATEGORY_OVERRIDES, __pickProject, 3, project, `project:${project.guid}`, routePage.project))
            : this.__fallbackCategoryNodes(USER_L3_CATEGORIES, __pickProject, 3, project.guid);

        return {
            id: `project:${project.guid}`,
            label: project.label,
            level: 2,
            entry: project,
            image: true,
            photoGuid: project.photoGuid,
            children: __children,
        };
    }

    // Bouwt één categorie-koknoop uit een live tab-snapshot: altijd klikbaar (entry + tabPreset,
    // navigeert naar de container en selecteert die tab), en uitklapbaar zodra er al bezochte items
    // voor die categorie zijn. Tabs zonder gevolgde navCategory (Photos/Description/Invitations, ...)
    // tonen als klikbare regel zonder kinderen. `buildProjectsChildren` geeft de domain-boom de kans
    // de rijkere project-structuur (eigen level3) te leveren voor de "Projects"-tab.
    private __tabCategoryNode(
        tab: IContainerTabSnapshot,
        overrides: Partial<Record<TabName, TNavCategory>>,
        pickItems: (category: TNavCategory) => INavHistoryEntry[],
        level: number,
        containerEntry: INavHistoryEntry,
        containerKey: string,
        routePageVal: TRoutePage,
        buildProjectsChildren?: (projects: INavHistoryEntry[]) => INavTreeNode[],
        indent?: number,
    ): INavTreeNode {
        const __mapped = overrides[tab.name] ?? tab.name;
        const __tabPreset = { routePage: routePageVal, tabName: tab.name };
        const __id = `${containerKey}|tab|${tab.name}`;

        if (buildProjectsChildren && __mapped === navCategory.projects) {
            return {
                id: __id,
                label: tab.label,
                level,
                entry: containerEntry,
                tabPreset: __tabPreset,
                children: buildProjectsChildren(pickItems(navCategory.projects)),
            };
        }

        const __items = isNavCategory(__mapped) ? pickItems(__mapped as TNavCategory) : [];
        return {
            id: __id,
            label: tab.label,
            level,
            ...(indent !== undefined ? { indent } : {}),
            entry: containerEntry,
            tabPreset: __tabPreset,
            children: this.__itemNodes(__items, level + 1, indent),
        };
    }

    // Terugval zonder snapshot (container zelf nog nooit bezocht, alleen indirect childs
    // geregistreerd): kop alleen tonen bij bestaande historie. Zonder `tabTarget` niet klikbaar/
    // tab-presetbaar. Mét `tabTarget` (admin: tabName == navCategory 1-op-1; domain: via
    // `overrides`, zie __categoryToTab) wél klikbaar zoals een snapshot-kop — anders leken de koppen onder AdminPanel tabs, maar deed klikken niets (een
    // kop zonder entry klapt alleen in/uit). Dit pad is na een account-wissel juist het gangbare:
    // clear() wist de snapshot, de server-hydratie herstelt alleen de historie.
    private __fallbackCategoryNodes(
        categories: readonly TNavCategory[],
        pickItems: (category: TNavCategory) => INavHistoryEntry[],
        level: number,
        containerKey: string,
        tabTarget?: { entry: INavHistoryEntry, routePage: TRoutePage, overrides?: Partial<Record<TabName, TNavCategory>> },
    ): INavTreeNode[] {
        return categories
            .map(category => ({ category, items: pickItems(category) }))
            .filter(g => g.items.length > 0)
            .map(g => ({
                id: `${containerKey}|grp|${g.category}`,
                label: NAV_CATEGORY_LABEL[g.category],
                level,
                ...(tabTarget ? {
                    entry: tabTarget.entry,
                    tabPreset: { routePage: tabTarget.routePage, tabName: this.__categoryToTab(g.category, tabTarget.overrides) },
                } : {}),
                children: this.__itemNodes(g.items, level + 1),
            }));
    }

    // Domain-fallback: L2-groepen + de "Projects"-groep (oude structuur, elk project met eigen
    // level3), alleen getoond zodra er al bezochte items zijn. Koppen zijn wél klikbaar (navigeren
    // naar het Domain en selecteren die tab), net als bij de admin-fallback — de snapshot is puur
    // lokaal, dus op een ander device of na een account-wissel is dit pad het gangbare, en een kop
    // die op een tab lijkt maar bij tikken alleen uit-/inklapt voelt als "navigeert niet".
    private __domainFallbackChildren(
        domain: INavHistoryEntry,
        pickDomain: (category: TNavCategory) => INavHistoryEntry[],
        entries: INavHistoryEntry[],
    ): INavTreeNode[] {
        const __children = this.__fallbackCategoryNodes(USER_L2_CATEGORIES, pickDomain, 1, domain.guid,
            { entry: domain, routePage: routePage.domain, overrides: DOMAIN_TAB_CATEGORY_OVERRIDES });

        // Projects is de eerste tab van DomainComponent — vooraan, gelijk aan de tab-dropdown.
        const __projects = pickDomain(navCategory.projects);
        if (__projects.length) {
            __children.unshift({
                id: `${domain.guid}|grp|${navCategory.projects}`,
                label: NAV_CATEGORY_LABEL[navCategory.projects],
                level: 1,
                entry: domain,
                tabPreset: { routePage: routePage.domain, tabName: tabName.projects },
                children: __projects.map(project => this.__projectNode(project, entries)),
            });
        }
        return __children;
    }

    // Omgekeerde van de tab→categorie-mapping in __tabCategoryNode: een categorie die zelf een
    // tabName is blijft gelijk (bv. contacts), anders de tab die er via `overrides` op mapt
    // (bv. projectCategories ← categories). Identiteit eerst, omdat een override ook een
    // tabName-sleutel kan hebben die op dit niveau niet bestaat (projectMembers → projects).
    private __categoryToTab(category: TNavCategory, overrides?: Partial<Record<TabName, TNavCategory>>): TabName {
        if ((Object.values(tabName) as string[]).includes(category)) return category as TabName;
        const __tab = Object.entries(overrides ?? {}).find(([, c]) => c === category)?.[0];
        return (__tab ?? category) as TabName;
    }

    // Zet op elke knoop de id's van z'n groepsgenoten (incl. zichzelf), recursief. Voedt het
    // accordeon-gedrag: bij openklappen sluit de UI de overige id's uit dezelfde groep.
    private __linkSiblings(nodes: INavTreeNode[]): INavTreeNode[] {
        const __ids = nodes.map(n => n.id);
        for (const __n of nodes) {
            __n.siblingIds = __ids;
            if (__n.children.length) this.__linkSiblings(__n.children);
        }
        return nodes;
    }

    // Items van één categorie als boom: wie een parentGuid heeft die ook in de lijst staat, komt onder die ouder (taak
    // in een taak); de rest staat bovenaan. Zonder parentGuid (alle andere categorieën) blijft het een platte lijst.
    // Heeft een bezochte pagina een tab-snapshot (`item:{guid}`, zie _TabPageRouteComponent.__navHistory_recordTabs), dan
    // krijgt hij zijn tabs als kinderen, net als Domain/Project; onder een tab staan de bezochte entries met parentGuid =
    // deze pagina (taak › tasks › taak). Zonder snapshot (nog niet bezocht op dit apparaat, of een gegarandeerde ouder)
    // hangen zulke kinderen direct onder de pagina, een inspring-stap verder (zie groupIndent).
    private __itemNodes(items: INavHistoryEntry[], level: number, baseIndent?: number): INavTreeNode[] {
        const __guids = new Set(items.map(it => it.guid));
        const __entries = this.entries$$();
        const __build = (it: INavHistoryEntry, lvl: number, indent: number, seen: Set<string>): INavTreeNode => {
            const __node: INavTreeNode = { ...this.__itemNode(it, lvl), indent };
            const __seen = new Set(seen).add(it.guid);
            const __snapshot = this.containerTabs$$()[`item:${it.guid}`];
            const __routePage = navCategoryRoutePage[it.category];

            if (__snapshot?.length && __routePage && lvl < 20) {
                const __pickChildren = (category: TNavCategory) => __entries
                    .filter(e => e.category === category && e.parentGuid === it.guid && !__seen.has(e.guid))
                    .sort((a, b) => b.visitedAt - a.visitedAt);
                __node.children = __snapshot.map(tab => this.__tabCategoryNode(
                    tab, ITEM_TAB_CATEGORY_OVERRIDES, __pickChildren, lvl + 1, it, `item:${it.guid}`, __routePage, undefined, indent + 1));
                return __node;
            }

            __node.children = items
                .filter(c => c.parentGuid === it.guid && !__seen.has(c.guid))
                .map(c => __build(c, lvl + 1, indent + 1, __seen));
            return __node;
        };
        return items
            .filter(it => !it.parentGuid || !__guids.has(it.parentGuid))
            .map(it => __build(it, level, baseIndent ?? Math.ceil(level / 2), new Set()));
    }

    // Blad-knoop voor een los item: klikbaar, met (eventueel leeg) foto-kader. sublabel toont
    // (indien aanwezig, nu alleen domainUsers/projectMembers) het e-mailadres onder de naam, zie entry.email.
    private __itemNode(entry: INavHistoryEntry, level: number): INavTreeNode {
        return {
            id: `${entry.category}:${entry.guid}`,
            label: entry.label,
            sublabel: entry.email ?? null,
            level,
            entry,
            image: true,
            photoGuid: entry.photoGuid,
            children: [],
        };
    }

    // Registreer een bezoek: upsert (zelfde category+guid+scope), nieuwste boven, cap per groep.
    // Een entry met dezelfde guid onder een ANDERE categorie is per definitie verouderd (guids zijn
    // uniek over alle entiteiten) — bv. een Tab die vóór de pages/tabs-splitsing (zie
    // navHistoryDescriptors[routePage.page].categoryOf) als "pages" geregistreerd stond.
    record(entry: Omit<INavHistoryEntry, 'visitedAt'>): void {
        const __next: INavHistoryEntry = { ...entry, visitedAt: Date.now() };
        const __rest = this.entries$$().filter(e => !this.__sameItem(e, __next)
            && !(e.guid === __next.guid && e.category !== __next.category));
        const __history = this.__cap([__next, ...__rest]);
        this.__store.appData$$.history.set(__history);
        this.__itemTabs_prune(__history);
    }

    // Tab-snapshots van bezochte pagina's (`item:{guid}`) opruimen zodra die pagina uit de geschiedenis valt; de
    // container-snapshots (admin/domain/project) blijven zoals ze waren.
    private __itemTabs_prune(history: INavHistoryEntry[]): void {
        const __tabs = this.containerTabs$$();
        const __guids = new Set(history.map(e => e.guid));
        const __stale = Object.keys(__tabs).filter(k => k.startsWith('item:') && !__guids.has(k.slice('item:'.length)));
        if (!__stale.length) return;
        const __next = { ...__tabs };
        __stale.forEach(k => delete __next[k]);
        this.__store.appData$$.containerTabs.set(__next);
    }

    // Zorgt dat een entry bestaat (bv. de bovenliggende taak van een bezochte taak) zonder een bestaande te overschrijven:
    // een echt bezoek levert rijkere gegevens (foto, eigen ouder) en die blijven staan.
    ensure(entry: Omit<INavHistoryEntry, 'visitedAt'>): void {
        if (this.entries$$().some(e => e.category === entry.category && e.guid === entry.guid)) return;
        this.record(entry);
    }

    // Upsert van de geautoriseerde-tabs-snapshot voor één container ('admin' / `domain:{guid}` /
    // `project:{guid}`) of bezochte pagina (`item:{guid}`) — aangeroepen vanuit _TabPageRouteComponent bij elk bezoek.
    containerTabs_record(key: string, tabs: IContainerTabSnapshot[]): void {
        this.__store.appData$$.containerTabs.set({ ...this.containerTabs$$(), [key]: tabs });
    }

    // Kiest de tab van de doelpagina, vóór het navigeren ernaartoe. Loopt via het frame van die
    // pagina in de navigatiestack — dezelfde tak die TabListService bij het laden al leest om de tab
    // te herstellen, dus de doelpagina hoeft niets te weten van de drawer.
    presetTab(routePath: string, tab: TabName): void {
        this.__navigationStackService.tabName_preset(routePath, tab);
    }

    // Wist zowel de platte geschiedenis als de per-container tab-snapshot (zie containerTabs$$
    // hierboven) — theme/i18n in dezelfde appData$$-boom blijven onaangeroerd.
    clear(): void {
        this.__store.appData$$.history.set([]);
        this.__store.appData$$.containerTabs.set({});
    }

    // Werkt het label van een bestaande entry direct bij (bv. na een naamswijziging), zonder de
    // volledige entry opnieuw op te bouwen zoals record() dat vereist — de geschiedenis-lijst is
    // een snapshot per bezoek (zie _tab-page-route-component.ts) en een naam-edit op de pagina
    // zelf triggert geen nieuwe record()-aanroep, dus zonder deze patch blijft het label in de
    // boom stil verouderd staan totdat de pagina opnieuw bezocht wordt.
    label_update(category: TNavCategory, guid: string, label: string): void {
        const __entries = this.entries$$();
        const __idx = __entries.findIndex(e => e.category === category && e.guid === guid);
        if (__idx === -1) return;
        const __next = [...__entries];
        __next[__idx] = { ...__next[__idx], label };
        this.__store.appData$$.history.set(__next);
    }

    // Werkt de photoGuid van een bestaande entry direct bij (na het zetten/verwijderen van de
    // hoofdfoto via de checkbox/drag in de Photos-tab) — spiegelt label_update hierboven: die
    // wijziging gebeurt in-place op een al bezochte pagina en triggert geen nieuwe record()-aanroep
    // (die loopt via navigatie, zie _tab-page-route-component.ts), dus zonder deze patch blijft de
    // oude foto in de geschiedenis staan totdat de pagina opnieuw bezocht wordt.
    // `photoIsUserFallback` optioneel meegeven zodra de aanroeper weet dat dit een expliciete,
    // eigen (domain-specifieke) fotokeuze is/was — undefined laat de bestaande vlag ongemoeid (zie
    // _TabPageService.mainPhoto_change).
    photoGuid_update(category: TNavCategory, guid: string, photoGuid: string | null, photoIsUserFallback?: boolean): void {
        const __entries = this.entries$$();
        const __idx = __entries.findIndex(e => e.category === category && e.guid === guid);
        if (__idx === -1) return;
        const __next = [...__entries];
        __next[__idx] = {
            ...__next[__idx],
            photoGuid,
            ...(photoIsUserFallback !== undefined ? { photoIsUserFallback } : {}),
        };
        this.__store.appData$$.history.set(__next);
    }

    // Propageert een wijziging van de eigen Home-hoofdfoto (User.PhotoFileBoxId) naar alle
    // domainUsers-drawer-entries die nog geen eigen domain-specifieke profielfoto hebben (fallback,
    // zie photoIsUserFallback/__domainUserPhotoGuid in nav-history-descriptors.ts). Nodig omdat Home
    // zelf geen eigen nav-history-entry heeft (zie HomeComponent.emit_ownMainPhotoChanged) — zonder
    // deze aparte ingang bleven die entries na een Home-fotowijziging stil verouderd staan, want de
    // generieke mainPhoto_change/photoGuid_update-flow patcht maar één entry (category+guid), niet elk
    // domein waarin je lid bent.
    userPhotoGuid_propagate(newGuid: string | null): void {
        const __entries = this.entries$$();
        let __changed = false;
        const __next = __entries.map(e => {
            if (e.category !== navCategory.domainUsers || !e.photoIsUserFallback) return e;
            __changed = true;
            return { ...e, photoGuid: newGuid };
        });
        if (__changed) this.__store.appData$$.history.set(__next);
    }

    // Puur-admin categorieën horen altijd dGuid=null te hebben (zie ADMIN_CATEGORIES/__pickAdmin
    // hierboven) — een entry met een dGuid is dus per definitie een scope-fout, ongeacht welke
    // domein-guid erin staat. Een domein-wortel die na deze correctie geen enkele niet-admin
    // entry (project/contact/domainUser/notice/...) meer aan zich gekoppeld heeft, bestond alleen
    // door die fout en wordt meeverwijderd.
    //
    // "Puur-admin" is bewust NIET simpelweg "staat in ADMIN_CATEGORIES": `functions` staat zowel in
    // ADMIN_CATEGORIES als in USER_L2_CATEGORIES — Function is dual-context (zie page-parent.ts en
    // _TabPageRouteComponent.__navHistory_record), dus een functions-entry kán een geldige domain-
    // scope hebben. Alleen categorieën die UITSLUITEND in ADMIN_CATEGORIES voorkomen worden hier
    // geforceerd op dGuid=null gezet.
    // Domain 0 (het systeemdomain, "Reflecs") is AdminPanel, geen gebruikersdomain. Oude entries van
    // vóór de admin-route (/admin-panel/domainUsers/{key}) liepen via /domains/{dauGuid}/..., kregen
    // daardoor de domain-0-guid als dGuid en lieten een Domain "Reflecs" in de drawer verschijnen.
    // Hier: de domain-0-wortel weg, de kinderen naar de admin-scope (dGuid null) en hun url naar de
    // admin-route — de entries zijn gesynct, dus zonder deze correctie kwamen ze steeds terug.
    private __repairSystemDomain(entries: INavHistoryEntry[]): INavHistoryEntry[] {
        const __systemGuid = this.__applicationUserService.systemDomainGuid$$();
        if (!__systemGuid) return entries;

        const __domainsPrefix = new RegExp(`^/${routeName.domains}/([^/?]+)/`);
        return entries
            .filter(e => !(e.category === navCategory.domains && e.guid === __systemGuid))
            .map(e => {
                if (e.dGuid !== __systemGuid) return e;
                const __match = e.url.match(__domainsPrefix);
                let __url = __match ? e.url.replace(__domainsPrefix, `/${routeName.adminPanel}/`) : e.url;
                if (__match && !__url.includes(`${routeKey.dauGuid}=`)) __url += `${__url.includes('?') ? '&' : '?'}${routeKey.dauGuid}=${__match[1]}`;
                return { ...e, dGuid: null, url: __url };
            });
    }

    private __repairAdminScope(entries: INavHistoryEntry[]): INavHistoryEntry[] {
        const __isAdminCategory = (c: TNavCategory) =>
            (ADMIN_CATEGORIES as readonly TNavCategory[]).includes(c)
            && !(USER_L2_CATEGORIES as readonly TNavCategory[]).includes(c)
            && !(USER_L3_CATEGORIES as readonly TNavCategory[]).includes(c);
        const __fixed = entries.map(e => __isAdminCategory(e.category) && e.dGuid !== null ? { ...e, dGuid: null } : e);
        const __liveDomainGuids = new Set(
            __fixed
                .filter(e => e.category !== navCategory.domains && !__isAdminCategory(e.category) && e.dGuid)
                .map(e => e.dGuid as string)
        );
        return __fixed.filter(e => e.category !== navCategory.domains || __liveDomainGuids.has(e.guid));
    }

    private __dedupe(entries: INavHistoryEntry[]): INavHistoryEntry[] {
        const __byKey = new Map<string, INavHistoryEntry>();
        for (const __e of entries) {
            const __key = `${__e.category}:${__e.guid}`;
            const __existing = __byKey.get(__key);
            if (!__existing || __e.visitedAt > __existing.visitedAt) __byKey.set(__key, __e);
        }
        return Array.from(__byKey.values());
    }

    private __sameItem(a: INavHistoryEntry, b: INavHistoryEntry): boolean {
        return a.category === b.category && a.guid === b.guid
            && a.dGuid === b.dGuid && a.oGuid === b.oGuid;
    }

    // Taken capt per ouder (parentGuid), zodat elke laag van de boom zijn eigen N nieuwste houdt.
    private __scopeKey(e: INavHistoryEntry): string {
        return `${e.category}|${e.dGuid ?? ''}|${e.oGuid ?? ''}|${e.parentGuid ?? ''}`;
    }

    // Sorteer nieuwste-eerst en behoud max N per (category, dGuid, oGuid).
    private __cap(entries: INavHistoryEntry[]): INavHistoryEntry[] {
        const __sorted = [...entries].sort((a, b) => b.visitedAt - a.visitedAt);
        const __counts = new Map<string, number>();
        const __kept: INavHistoryEntry[] = [];
        for (const __e of __sorted) {
            const __key = this.__scopeKey(__e);
            const __n = __counts.get(__key) ?? 0;
            if (__n >= NAV_HISTORY_MAX_PER_GROUP) continue;
            __counts.set(__key, __n + 1);
            __kept.push(__e);
        }
        return __kept;
    }

    // Items van één categorie binnen één scope, nieuwste-eerst (al gecapt bij opslag).
    private __pick(
        category: TNavCategory,
        dGuid: string | null,
        oGuid: string | null,
        entries: INavHistoryEntry[] = this.entries$$(),
    ): INavHistoryEntry[] {
        return entries
            .filter(e => e.category === category && e.dGuid === dGuid && e.oGuid === oGuid)
            .sort((a, b) => b.visitedAt - a.visitedAt);
    }

    // Level3-items van één project, alleen op project-guid (oGuid) — zonder domein-scope, zie userTree$$.
    private __pickByProject(
        category: TNavCategory,
        oGuid: string,
        entries: INavHistoryEntry[] = this.entries$$(),
    ): INavHistoryEntry[] {
        // Zonder entries die onder een ouder van een ander soort hangen (melding in een taak): die staan onder de taak.
        return entries
            .filter(e => e.category === category && e.oGuid === oGuid && (!e.parentCategory || e.parentCategory === category))
            .sort((a, b) => b.visitedAt - a.visitedAt);
    }
}
